import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../hooks/useAuth';

// Types
interface IconAccessories {
  hat?: string;
  glasses?: string;
  shirt?: string;
  accessory?: string;
  background_color?: string;
}

interface AgentIcon {
  base: string;
  accessories?: IconAccessories;
}

interface Agent {
  agent_id: string;
  name: string;
  description: string;
  instructions: string;
  tool_sets: string[];
  sample_questions: string[];
  icon?: AgentIcon;
}

interface AccessoryOption {
  id: string;
  name: string;
  emoji: string;
}

interface Accessories {
  hats: AccessoryOption[];
  glasses: AccessoryOption[];
  shirts: AccessoryOption[];
  accessories: AccessoryOption[];
  background_colors: { id: string; name: string }[];
}

interface ToolSet {
  id: string;
  description: string;
  tools: string[];
}

const API_BASE = process.env.REACT_APP_API_URL || '';

export const AgentFactory: React.FC = () => {
  const { isAuthenticated } = useAuth();
  const [agents, setAgents] = useState<Agent[]>([]);
  const [toolSets, setToolSets] = useState<ToolSet[]>([]);
  const [accessories, setAccessories] = useState<Accessories | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedAgent, setSelectedAgent] = useState<Agent | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [testMessage, setTestMessage] = useState('');
  const [testResponse, setTestResponse] = useState<string | null>(null);
  const [testLoading, setTestLoading] = useState(false);

  // Form state for create/edit
  const [formData, setFormData] = useState<Partial<Agent>>({
    agent_id: '',
    name: '',
    description: '',
    instructions: '',
    tool_sets: [],
    sample_questions: [],
    icon: { base: 'aldo', accessories: {} }
  });

  const fetchAgents = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE}/api/admin/agents`, {
        credentials: 'include'
      });
      if (!response.ok) throw new Error('Failed to fetch agents');
      const data = await response.json();
      setAgents(data.agents || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load agents');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchToolSets = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE}/api/admin/tool-sets`, {
        credentials: 'include'
      });
      if (!response.ok) throw new Error('Failed to fetch tool sets');
      const data = await response.json();
      setToolSets(data.tool_sets || []);
    } catch (err) {
      console.error('Failed to load tool sets:', err);
    }
  }, []);

  const fetchAccessories = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE}/api/admin/accessories`, {
        credentials: 'include'
      });
      if (!response.ok) throw new Error('Failed to fetch accessories');
      const data = await response.json();
      setAccessories(data);
    } catch (err) {
      console.error('Failed to load accessories:', err);
    }
  }, []);

  useEffect(() => {
    fetchAgents();
    fetchToolSets();
    fetchAccessories();
  }, [fetchAgents, fetchToolSets, fetchAccessories]);

  const handleCreate = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/admin/agents`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(formData)
      });
      
      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.detail || 'Failed to create agent');
      }
      
      await fetchAgents();
      setIsCreating(false);
      resetForm();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create agent');
    }
  };

  const handleUpdate = async () => {
    if (!selectedAgent) return;
    
    try {
      const response = await fetch(`${API_BASE}/api/admin/agents/${selectedAgent.agent_id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(formData)
      });
      
      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.detail || 'Failed to update agent');
      }
      
      await fetchAgents();
      setIsEditing(false);
      setSelectedAgent(null);
      resetForm();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update agent');
    }
  };

  const handleDelete = async (agentId: string) => {
    if (!window.confirm(`Are you sure you want to delete agent "${agentId}"?`)) return;
    
    try {
      const response = await fetch(`${API_BASE}/api/admin/agents/${agentId}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      
      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.detail || 'Failed to delete agent');
      }
      
      await fetchAgents();
      if (selectedAgent?.agent_id === agentId) {
        setSelectedAgent(null);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete agent');
    }
  };

  const handleTest = async () => {
    if (!selectedAgent || !testMessage.trim()) return;
    
    setTestLoading(true);
    setTestResponse(null);
    
    try {
      const response = await fetch(`${API_BASE}/api/admin/agents/${selectedAgent.agent_id}/test`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ message: testMessage })
      });
      
      const data = await response.json();
      setTestResponse(data.response || data.error || 'No response');
    } catch (err) {
      setTestResponse(`Error: ${err instanceof Error ? err.message : 'Test failed'}`);
    } finally {
      setTestLoading(false);
    }
  };

  const handleReload = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/admin/reload`, {
        method: 'POST',
        credentials: 'include'
      });
      
      if (!response.ok) throw new Error('Failed to reload agents');
      
      const data = await response.json();
      alert(`Agents reloaded: ${data.agents_loaded?.join(', ') || 'none'}`);
      await fetchAgents();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to reload agents');
    }
  };

  const resetForm = () => {
    setFormData({
      agent_id: '',
      name: '',
      description: '',
      instructions: '',
      tool_sets: [],
      sample_questions: [],
      icon: { base: 'aldo', accessories: {} }
    });
  };

  const startEdit = (agent: Agent) => {
    setSelectedAgent(agent);
    setFormData({ ...agent });
    setIsEditing(true);
    setIsCreating(false);
  };

  const startCreate = () => {
    resetForm();
    setSelectedAgent(null);
    setIsCreating(true);
    setIsEditing(false);
  };

  const renderIconPreview = (icon?: AgentIcon) => {
    const bgColor = icon?.accessories?.background_color || '#3B82F6';
    const hat = accessories?.hats.find(h => h.id === icon?.accessories?.hat);
    const glasses = accessories?.glasses.find(g => g.id === icon?.accessories?.glasses);
    const shirt = accessories?.shirts.find(s => s.id === icon?.accessories?.shirt);
    const accessory = accessories?.accessories.find(a => a.id === icon?.accessories?.accessory);
    
    return (
      <div 
        className="w-16 h-16 rounded-full flex items-center justify-center text-white text-2xl relative"
        style={{ backgroundColor: bgColor }}
      >
        <span className="text-3xl">🤖</span>
        {hat?.emoji && <span className="absolute -top-2 left-1/2 -translate-x-1/2 text-lg">{hat.emoji}</span>}
        {glasses?.emoji && <span className="absolute top-4 left-1/2 -translate-x-1/2 text-sm">{glasses.emoji}</span>}
        {shirt?.emoji && <span className="absolute -bottom-1 left-1/2 -translate-x-1/2 text-sm">{shirt.emoji}</span>}
        {accessory?.emoji && <span className="absolute -right-1 top-1/2 -translate-y-1/2 text-sm">{accessory.emoji}</span>}
      </div>
    );
  };

  if (!isAuthenticated) {
    return (
      <div className="h-full flex items-center justify-center bg-gray-900 text-white">
        <div className="text-center">
          <h1 className="text-2xl font-bold mb-4">🔒 Authentication Required</h1>
          <p>Please sign in to access the Agent Factory.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full overflow-y-auto bg-gray-900 text-white p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-pink-500">
              🏭 Agent Factory
            </h1>
            <p className="text-gray-400 mt-1">Create and manage custom AI agents</p>
          </div>
          <div className="flex gap-3">
            <button
              onClick={handleReload}
              className="px-4 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg transition-colors"
            >
              🔄 Reload Agents
            </button>
            <button
              onClick={startCreate}
              className="px-4 py-2 bg-purple-600 hover:bg-purple-500 rounded-lg transition-colors"
            >
              ➕ Create Agent
            </button>
          </div>
        </div>

        {/* Error Banner */}
        {error && (
          <div className="mb-6 p-4 bg-red-900/50 border border-red-500 rounded-lg">
            <p className="text-red-200">{error}</p>
            <button onClick={() => setError(null)} className="text-red-400 underline mt-2">Dismiss</button>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Agent List */}
          <div className="lg:col-span-1">
            <div className="bg-gray-800 rounded-xl p-4">
              <h2 className="text-lg font-semibold mb-4">📋 Agents ({agents.length})</h2>
              
              {loading ? (
                <div className="text-center py-8 text-gray-400">Loading...</div>
              ) : agents.length === 0 ? (
                <div className="text-center py-8 text-gray-400">
                  No agents configured yet.
                  <br />
                  <button onClick={startCreate} className="text-purple-400 underline mt-2">
                    Create your first agent
                  </button>
                </div>
              ) : (
                <div className="space-y-2">
                  {agents.map(agent => (
                    <div
                      key={agent.agent_id}
                      onClick={() => { setSelectedAgent(agent); setIsEditing(false); setIsCreating(false); }}
                      className={`p-3 rounded-lg cursor-pointer transition-colors flex items-center gap-3 ${
                        selectedAgent?.agent_id === agent.agent_id
                          ? 'bg-purple-600/30 border border-purple-500'
                          : 'bg-gray-700/50 hover:bg-gray-700'
                      }`}
                    >
                      {renderIconPreview(agent.icon)}
                      <div className="flex-1 min-w-0">
                        <div className="font-medium truncate">{agent.name}</div>
                        <div className="text-xs text-gray-400 truncate">{agent.agent_id}</div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Agent Detail / Form */}
          <div className="lg:col-span-2">
            {isCreating || isEditing ? (
              <div className="bg-gray-800 rounded-xl p-6">
                <h2 className="text-xl font-semibold mb-6">
                  {isCreating ? '➕ Create New Agent' : `✏️ Edit ${selectedAgent?.name}`}
                </h2>
                
                <div className="space-y-6">
                  {/* Basic Info */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm text-gray-400 mb-1">Agent ID</label>
                      <input
                        type="text"
                        value={formData.agent_id || ''}
                        onChange={e => setFormData({ ...formData, agent_id: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '_') })}
                        disabled={isEditing}
                        className="w-full bg-gray-700 rounded-lg px-3 py-2 disabled:opacity-50"
                        placeholder="my_agent"
                      />
                    </div>
                    <div>
                      <label className="block text-sm text-gray-400 mb-1">Display Name</label>
                      <input
                        type="text"
                        value={formData.name || ''}
                        onChange={e => setFormData({ ...formData, name: e.target.value })}
                        className="w-full bg-gray-700 rounded-lg px-3 py-2"
                        placeholder="My Custom Agent"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm text-gray-400 mb-1">Description</label>
                    <input
                      type="text"
                      value={formData.description || ''}
                      onChange={e => setFormData({ ...formData, description: e.target.value })}
                      className="w-full bg-gray-700 rounded-lg px-3 py-2"
                      placeholder="What does this agent do?"
                    />
                  </div>

                  <div>
                    <label className="block text-sm text-gray-400 mb-1">Instructions (System Prompt)</label>
                    <textarea
                      value={formData.instructions || ''}
                      onChange={e => setFormData({ ...formData, instructions: e.target.value })}
                      className="w-full bg-gray-700 rounded-lg px-3 py-2 h-32 font-mono text-sm"
                      placeholder="You are a helpful assistant that..."
                    />
                  </div>

                  {/* Icon Builder */}
                  <div>
                    <label className="block text-sm text-gray-400 mb-2">🎨 Icon Customization</label>
                    <div className="bg-gray-700/50 rounded-lg p-4">
                      <div className="flex items-start gap-6">
                        <div className="flex-shrink-0">
                          {renderIconPreview(formData.icon)}
                        </div>
                        {accessories && (
                          <div className="flex-1 grid grid-cols-2 gap-3 text-sm">
                            <div>
                              <label className="text-gray-400">Hat</label>
                              <select
                                value={formData.icon?.accessories?.hat || 'none'}
                                onChange={e => setFormData({
                                  ...formData,
                                  icon: {
                                    ...formData.icon!,
                                    accessories: { ...formData.icon?.accessories, hat: e.target.value }
                                  }
                                })}
                                className="w-full bg-gray-600 rounded px-2 py-1 mt-1"
                              >
                                {accessories.hats.map(h => (
                                  <option key={h.id} value={h.id}>{h.emoji} {h.name}</option>
                                ))}
                              </select>
                            </div>
                            <div>
                              <label className="text-gray-400">Glasses</label>
                              <select
                                value={formData.icon?.accessories?.glasses || 'none'}
                                onChange={e => setFormData({
                                  ...formData,
                                  icon: {
                                    ...formData.icon!,
                                    accessories: { ...formData.icon?.accessories, glasses: e.target.value }
                                  }
                                })}
                                className="w-full bg-gray-600 rounded px-2 py-1 mt-1"
                              >
                                {accessories.glasses.map(g => (
                                  <option key={g.id} value={g.id}>{g.emoji} {g.name}</option>
                                ))}
                              </select>
                            </div>
                            <div>
                              <label className="text-gray-400">Shirt</label>
                              <select
                                value={formData.icon?.accessories?.shirt || 'none'}
                                onChange={e => setFormData({
                                  ...formData,
                                  icon: {
                                    ...formData.icon!,
                                    accessories: { ...formData.icon?.accessories, shirt: e.target.value }
                                  }
                                })}
                                className="w-full bg-gray-600 rounded px-2 py-1 mt-1"
                              >
                                {accessories.shirts.map(s => (
                                  <option key={s.id} value={s.id}>{s.emoji} {s.name}</option>
                                ))}
                              </select>
                            </div>
                            <div>
                              <label className="text-gray-400">Accessory</label>
                              <select
                                value={formData.icon?.accessories?.accessory || 'none'}
                                onChange={e => setFormData({
                                  ...formData,
                                  icon: {
                                    ...formData.icon!,
                                    accessories: { ...formData.icon?.accessories, accessory: e.target.value }
                                  }
                                })}
                                className="w-full bg-gray-600 rounded px-2 py-1 mt-1"
                              >
                                {accessories.accessories.map(a => (
                                  <option key={a.id} value={a.id}>{a.emoji} {a.name}</option>
                                ))}
                              </select>
                            </div>
                            <div className="col-span-2">
                              <label className="text-gray-400">Background Color</label>
                              <div className="flex gap-2 mt-1 flex-wrap">
                                {accessories.background_colors.map(c => (
                                  <button
                                    key={c.id}
                                    onClick={() => setFormData({
                                      ...formData,
                                      icon: {
                                        ...formData.icon!,
                                        accessories: { ...formData.icon?.accessories, background_color: c.id }
                                      }
                                    })}
                                    className={`w-8 h-8 rounded-full border-2 ${
                                      formData.icon?.accessories?.background_color === c.id
                                        ? 'border-white'
                                        : 'border-transparent'
                                    }`}
                                    style={{ backgroundColor: c.id }}
                                    title={c.name}
                                  />
                                ))}
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Tool Sets */}
                  <div>
                    <label className="block text-sm text-gray-400 mb-2">🔧 Tool Sets</label>
                    <div className="flex flex-wrap gap-2">
                      {toolSets.map(ts => (
                        <label
                          key={ts.id}
                          className={`px-3 py-1.5 rounded-full cursor-pointer transition-colors ${
                            formData.tool_sets?.includes(ts.id)
                              ? 'bg-purple-600 text-white'
                              : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={formData.tool_sets?.includes(ts.id) || false}
                            onChange={e => {
                              const newSets = e.target.checked
                                ? [...(formData.tool_sets || []), ts.id]
                                : (formData.tool_sets || []).filter(s => s !== ts.id);
                              setFormData({ ...formData, tool_sets: newSets });
                            }}
                            className="hidden"
                          />
                          {ts.id}
                        </label>
                      ))}
                      {toolSets.length === 0 && (
                        <span className="text-gray-500">No tool sets available</span>
                      )}
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex gap-3 pt-4 border-t border-gray-700">
                    <button
                      onClick={() => { setIsCreating(false); setIsEditing(false); resetForm(); }}
                      className="px-4 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={isCreating ? handleCreate : handleUpdate}
                      className="px-4 py-2 bg-purple-600 hover:bg-purple-500 rounded-lg"
                    >
                      {isCreating ? 'Create Agent' : 'Save Changes'}
                    </button>
                  </div>
                </div>
              </div>
            ) : selectedAgent ? (
              <div className="bg-gray-800 rounded-xl p-6">
                <div className="flex items-start gap-4 mb-6">
                  {renderIconPreview(selectedAgent.icon)}
                  <div className="flex-1">
                    <h2 className="text-2xl font-bold">{selectedAgent.name}</h2>
                    <p className="text-gray-400">{selectedAgent.agent_id}</p>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => startEdit(selectedAgent)}
                      className="px-3 py-1.5 bg-blue-600 hover:bg-blue-500 rounded-lg text-sm"
                    >
                      ✏️ Edit
                    </button>
                    <button
                      onClick={() => handleDelete(selectedAgent.agent_id)}
                      className="px-3 py-1.5 bg-red-600 hover:bg-red-500 rounded-lg text-sm"
                    >
                      🗑️ Delete
                    </button>
                  </div>
                </div>

                <div className="space-y-4">
                  <div>
                    <h3 className="text-sm text-gray-400 mb-1">Description</h3>
                    <p>{selectedAgent.description || 'No description'}</p>
                  </div>

                  <div>
                    <h3 className="text-sm text-gray-400 mb-1">Tool Sets</h3>
                    <div className="flex flex-wrap gap-2">
                      {selectedAgent.tool_sets?.map(ts => (
                        <span key={ts} className="px-2 py-1 bg-purple-600/30 rounded text-sm">{ts}</span>
                      )) || <span className="text-gray-500">None</span>}
                    </div>
                  </div>

                  <div>
                    <h3 className="text-sm text-gray-400 mb-1">Sample Questions</h3>
                    <ul className="list-disc list-inside text-sm">
                      {selectedAgent.sample_questions?.map((q, i) => (
                        <li key={i} className="text-gray-300">{q}</li>
                      )) || <li className="text-gray-500">None</li>}
                    </ul>
                  </div>

                  {/* Test Agent */}
                  <div className="pt-4 border-t border-gray-700">
                    <h3 className="text-sm text-gray-400 mb-2">🧪 Test Agent</h3>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={testMessage}
                        onChange={e => setTestMessage(e.target.value)}
                        placeholder="Type a test message..."
                        className="flex-1 bg-gray-700 rounded-lg px-3 py-2"
                        onKeyDown={e => e.key === 'Enter' && handleTest()}
                      />
                      <button
                        onClick={handleTest}
                        disabled={testLoading || !testMessage.trim()}
                        className="px-4 py-2 bg-green-600 hover:bg-green-500 disabled:opacity-50 rounded-lg"
                      >
                        {testLoading ? '...' : 'Send'}
                      </button>
                    </div>
                    {testResponse && (
                      <div className="mt-3 p-3 bg-gray-700/50 rounded-lg">
                        <pre className="text-sm whitespace-pre-wrap">{testResponse}</pre>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <div className="bg-gray-800 rounded-xl p-12 text-center">
                <div className="text-6xl mb-4">🤖</div>
                <h2 className="text-xl font-semibold mb-2">Select an Agent</h2>
                <p className="text-gray-400">
                  Choose an agent from the list to view details, or create a new one.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AgentFactory;
